/**
 * The app's simple dialogs (NEWS-297; kerf UI, NM-SX7NCY): confirm, export,
 * the disambiguation sense picker, "did you mean?", Edit topic, capacity, and
 * the mock checkout.
 *
 * Every dialog is the same composition — kerf UI's `DialogSurface`, a native
 * `<dialog>` since kerf UI 5.0.0-beta.106 — made by `appDialog`. kerf UI owns
 * the modal behaviour: the top layer, the inert page, Escape, the header close
 * button, and (with `lightDismiss`) a press on the backdrop. The app owns the
 * open state: a dialog renders while its store state is set, `syncDialogs` in
 * `app.tsx` opens each rendered one with `openDialog`, and when kerf UI closes
 * it the `close` listener there clears that state by the dialog's
 * `data-dialog` key. A Cancel button clears the state directly, which removes
 * the dialog.
 *
 * Rendering only — every `data-action=*` / `data-*` click is handled by
 * `delegate()` in `app.tsx` (NEWS-126).
 *
 * **The privacy dialog moved out** (NM-211): "what your data is and where it
 * goes" is its own full page at `/privacy`, linked from the page footer.
 */

import type { KerfUiContent } from '@kerfjs/ui';
import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { Checkbox } from '@kerfjs/ui/checkbox';
import { ChoiceChips } from '@kerfjs/ui/choice-chips';
import { Disclosure } from '@kerfjs/ui/disclosure';
import { List } from '@kerfjs/ui/list';
import { ListItem } from '@kerfjs/ui/list-item';
import { Row } from '@kerfjs/ui/row';
import { Select } from '@kerfjs/ui/select';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface, type DialogSurfaceSize } from '@kerfjs/ui/surface-scaffold';
import { FieldLabel, Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import type { StateResp } from '../../../api/schemas.js';
import { MOCK_TEST_CARDS } from '../../../billing/mock-billing.js';
import { activeCategories, BUILTIN_CATEGORIES, findCategory } from '../../../categories.js';
import type { Topic } from '../../../db/schemas.js';
import { cadenceLine } from '../../../plans.js';
import { FOCUS_OPTIONS } from '../../../topic-focus.js';
import type { AppState, CapacityDialog, CheckoutState, TopicSuggest } from '../../app/stores.js';
import { exportHref } from '../../features/export/export-url.js';
import {
  canSubmitDisable,
  canSubmitEnable,
  disableCounterText,
  enableCounterText,
  kindCapacity,
  needToDisable,
  type PlanBlock,
  topicKind,
} from '../../features/topics/capacity.js';
import { icon } from '../icons.js';

/** Every dialog's key, which is its `data-dialog` and its `DialogSurface` identity. */
export type DialogKey =
  | 'confirm'
  | 'export'
  | 'sense'
  | 'suggest'
  | 'rename'
  | 'capacity'
  | 'checkout'
  | 'settings'
  | 'discover'
  | 'onboarding'
  | 'pulse'
  | 'add-topic';

/**
 * One dialog: a kerf UI `DialogSurface`.
 *
 * `label` names the dialog (its header title and accessible name). The body is
 * a `List` on the surface's standard body inset — fields sit on it, and bare
 * prose joins their text column through `ListInsetText`; `footer` is the
 * dialog's footer, right-aligned. Sibling dialogs are told apart by a stable
 * `data-key` on the surface root, so a re-render never morphs one into another.
 * Confirmations use the supported alert prop and are described by their message.
 */
export function appDialog(opts: {
  key: DialogKey;
  label: string;
  body: KerfUiContent;
  footer: KerfUiContent;
  size?: DialogSurfaceSize;
  alert?: boolean;
  describedBy?: string;
  /** Close on a press outside the panel. Off for a dialog holding unsaved input. */
  lightDismiss?: boolean;
}): SafeHtml {
  return (
    <DialogSurface
      label={opts.label}
      alert={opts.alert}
      describedBy={opts.describedBy}
      rootAttributes={{ 'data-key': `dialog-${opts.key}`, 'data-dialog': opts.key }}
      size={opts.size ?? 'medium'}
      lightDismiss={opts.lightDismiss !== false}
      footer={
        <Row gap="xs" hAlign="right" wrap>
          {opts.footer}
        </Row>
      }
    >
      <List gap="m" rootAttributes={{ 'data-dialog-body': '' }}>
        {opts.body}
      </List>
    </DialogSurface>
  );
}

/**
 * Supporting prose in a dialog body. A block `Text` carries the standard item
 * inset (1px + 8px), which puts it on the same text column as a field's label.
 */
function hint(text: string | SafeHtml): SafeHtml {
  return (
    <Text tone="quiet" size="compact">
      {text}
    </Text>
  );
}

/**
 * The in-app confirmation (never `window.confirm`, NEWS-39).
 *
 * A danger confirmation leads with a danger `StateBanner` saying it can't be
 * undone, so the weight sits on the confirm rather than on the trigger
 * (NM-101). The dialog's title already names the action, so the banner does not
 * repeat it (NM-MTVD93). The confirm button can hold a countdown — disabled, showing the
 * seconds left — as a speed bump before an irreversible bulk delete.
 */
export function confirmDialogJsx(c: NonNullable<AppState['confirm']>): SafeHtml {
  const holding = c.remaining > 0;
  const label = holding ? `${c.confirmLabel} (${String(c.remaining)})` : c.confirmLabel;
  return appDialog({
    key: 'confirm',
    alert: true,
    describedBy: 'confirm-message',
    lightDismiss: false,
    label: c.title ?? 'Confirm',
    size: 'small',
    body: [
      c.danger && c.title !== null ? (
        <StateBanner tone="danger" icon={icon('warn')} title="This Can’t Be Undone" />
      ) : null,
      <Text id="confirm-message" data-confirm-message="">
        {c.message}
      </Text>,
    ],
    footer: [
      <Button emphasis="secondary" action="confirm-cancel">
        Cancel
      </Button>,
      <Button tone={c.danger ? 'danger' : 'brand'} action="confirm-ok" disabled={holding}>
        {label}
      </Button>,
    ],
  });
}

/**
 * The export dialog (NEWS-158): two questions — what, and in which format —
 * so every scope × format combination is reachable. The export runs as a
 * fetch-blob download (NM-101), so the button can say "Exporting…" while it
 * runs; `exportHref` returns null when the choice can't be exported ("one
 * topic" with none picked), and the button is then disabled for that reason.
 */
export function exportDialogJsx(state: NonNullable<AppState['export']>, topics: Topic[], exporting: boolean): SafeHtml {
  const { scope, topicId, format } = state;
  const href = exportHref(state);
  const scopeHints: Record<string, string> = {
    all: 'Everything kept, newest first.',
    saved: 'Just your bookmarks.',
    topic: topics.length === 0 ? 'No topics to export yet.' : 'Everything found for a single subject.',
  };
  return appDialog({
    key: 'export',
    label: 'Export Stories',
    body: [
      <ChoiceChips
        name="export-scope"
        label="What to Export"
        action="export-scope"
        value={scope}
        choices={[
          { value: 'all', label: 'All Stories' },
          { value: 'saved', label: 'Saved Only' },
          // Nothing to narrow to, so the option would only ever produce an empty file (NEWS-160).
          { value: 'topic', label: 'One Topic', disabled: topics.length === 0 },
        ]}
      />,
      hint(scopeHints[scope] ?? ''),
      // Say why "One Topic" is unavailable, beside the control (NEWS-309).
      topics.length === 0 && scope !== 'topic' ? hint('One Topic is unavailable: no topics to export yet.') : null,
      // Always present (the slot), filled only while "one topic" is the scope.
      <div data-export-topic-slot="">
        {scope === 'topic' && topics.length > 0 ? (
          <Select
            name="export-topic"
            fitMenu
            label="Topic"
            value={topicId ?? ''}
            placeholderText="Choose a topic"
            choices={topics.map((topic) => ({ value: topic.id, label: topic.name }))}
          />
        ) : (
          ''
        )}
      </div>,
      <ChoiceChips
        name="export-format"
        label="Format"
        action="export-format"
        value={format}
        choices={[
          { value: 'md', label: 'Markdown', title: 'Grouped by topic, for pasting into notes' },
          { value: 'json', label: 'JSON', title: 'The escape hatch — every field, topic names not ids' },
        ]}
      />,
      hint(
        format === 'md'
          ? 'Markdown: grouped by topic, for pasting into notes. Off-topic stories are left out, as they are in the feed. Up to 2000 stories.'
          : 'JSON: every field, with topic names rather than ids. Off-topic stories are left out, as they are in the feed. Up to 2000 stories.',
      ),
    ],
    footer: [
      <Button emphasis="secondary" action="close-export">
        Cancel
      </Button>,
      href === null ? (
        <Button tone="brand" disabled>
          Export
        </Button>
      ) : (
        <Button tone="brand" disabled={exporting} rootAttributes={{ 'data-export-url': href }}>
          {exporting ? 'Exporting…' : 'Export'}
        </Button>
      ),
    ],
  });
}

/**
 * The disambiguation **sense picker** for an ambiguous topic (NM-163,
 * docs/47). Auto-opened when an ambiguous topic is added, and re-opened by
 * clicking its needs-attention row. One click on a sense is the whole action.
 * Senses this account already follows a fork of are disabled (NM-164).
 */
export function sensePickerDialogJsx(topic: Topic, usedSenses: readonly string[] = []): SafeHtml {
  const used = new Set(usedSenses);
  return appDialog({
    key: 'sense',
    label: `Which “${topic.name}” Do You Mean?`,
    body: [
      hint(
        'That name can mean more than one thing. Pick the sense you want news about — checking won’t start until you choose.',
      ),
      <List gap="2xs" rootAttributes={{ 'data-sense-options': '' }}>
        {topic.senseOptions.map((sense, i) => {
          const already = used.has(sense);
          return (
            <ListItem
              action="pick-sense"
              label={sense}
              status={already ? 'Already following' : undefined}
              disabled={already}
              multiline
              rootAttributes={{
                'data-key': `sense-${String(i)}`,
                'data-sense-topic': topic.id,
                'data-sense-index': String(i),
                'data-sense-used': already ? 'true' : undefined,
              }}
            />
          );
        })}
      </List>,
    ],
    footer: [
      <Button emphasis="secondary" action="close-sense">
        Not Now
      </Button>,
    ],
  });
}

/**
 * The capacity dialog (NM-201, docs/48 FR-48.6) — one dialog, two modes.
 * `disable`: the reader tried to enable a subject over the plan cap, so they
 * pick enabled subjects of that kind to disable; submitting commits the swap.
 * `enable`: the upgrade prompt — free room opened up, so they pick disabled
 * subjects to turn back on. Rows are pressed-state `ListItem` toggles; the
 * ticked set lives in the store, so a poll re-render keeps every tick.
 */
export function capacityDialogJsx(dialog: CapacityDialog, topics: Topic[], plan: PlanBlock): SafeHtml {
  const picked = new Set(dialog.picked);
  const isDisable = dialog.mode === 'disable';
  const candidates = isDisable
    ? topics.filter((t) => !t.paused && topicKind(t) === dialog.kind)
    : topics.filter((t) => t.paused);
  const pickedTopics = candidates.filter((t) => picked.has(t.id));

  const { cap, used } = kindCapacity(plan, dialog.kind);
  const need = isDisable ? needToDisable(cap, used, dialog.enableIds.length) : 0;
  const canSubmit = isDisable ? canSubmitDisable(picked.size, need) : canSubmitEnable(pickedTopics, plan);
  const counter = isDisable ? disableCounterText(picked.size, need) : enableCounterText(picked.size, plan);
  const kindLabel = dialog.kind === 'catalog' ? 'catalog' : 'custom';

  return appDialog({
    key: 'capacity',
    label: isDisable ? `You’re at Your ${plan.label} Plan’s Limit` : 'You Have Room for More Topics',
    lightDismiss: false,
    body: [
      hint(
        isDisable
          ? `Your ${plan.label} plan tracks ${String(cap ?? 0)} ${kindLabel} topics at a time. Pick ${need === 1 ? 'one' : String(need)} to disable, and the topic you were enabling turns on.`
          : 'A disabled topic shows no articles and is never checked. Pick which ones to turn back on.',
      ),
      // Select-all only when re-enabling (a bounded pick); disabling is "pick exactly enough".
      isDisable ? null : (
        <Row gap="xs" controlInsets="rl">
          <Button size="compact" emphasis="secondary" action="capacity-select-all">
            Select as Many as Fit
          </Button>
          <Button size="compact" emphasis="plain" action="capacity-select-none">
            Clear
          </Button>
        </Row>
      ),
      candidates.length === 0 ? (
        hint(isDisable ? 'No other topics to disable.' : 'No disabled topics.')
      ) : (
        <List gap="2xs" rootAttributes={{ 'data-capacity-list': '' }}>
          {candidates.map((t) => (
            <ListItem
              action="capacity-pick"
              label={t.name}
              status={topicKind(t)}
              pressed={picked.has(t.id)}
              icon={picked.has(t.id) ? icon('ok') : icon('clear')}
              rootAttributes={{ 'data-key': t.id, 'data-capacity-pick': t.id }}
            />
          ))}
        </List>
      ),
      <Text tone="quiet" size="compact" aria-live="polite" data-capacity-counter="">
        {counter}
      </Text>,
    ],
    footer: [
      <Button emphasis="secondary" action="close-capacity">
        Cancel
      </Button>,
      <Button tone="brand" action="capacity-submit" disabled={!canSubmit}>
        {isDisable ? 'Disable & Enable' : 'Enable Selected'}
      </Button>,
    ],
  });
}

/**
 * The "did you mean?" dialog for a typed add (NM-184). Spelling corrections
 * (NM-179) first, then similar existing subjects to follow (NM-148/153). The
 * typed name's "Adding…" placeholder is already in the rail; whichever action
 * the reader takes reconciles it.
 */
export function suggestDialogJsx(suggest: TopicSuggest): SafeHtml {
  const hasCorrections = suggest.corrections.length > 0;
  const hasSuggestions = suggest.suggestions.length > 0;
  return appDialog({
    key: 'suggest',
    label: 'Did You Mean?',
    lightDismiss: false,
    body: [
      hasCorrections ? (
        <List gap="2xs" rootAttributes={{ 'data-suggest-corrections': '' }}>
          {suggest.corrections.map((corr) => (
            <ListItem
              action="suggest-correct"
              label={corr.name}
              description={corr.descriptor === '' ? undefined : corr.descriptor}
              multiline
              rootAttributes={{ 'data-key': `correct:${corr.name}`, 'data-suggest-correct': corr.name }}
            />
          ))}
        </List>
      ) : null,
      hasSuggestions
        ? hint(
            suggest.strongMatch !== null
              ? `“${suggest.strongMatch.name}” looks like the same subject as what you typed. Following it joins one shared check — fresher, and without a duplicate.`
              : 'Similar topics already exist. Following a shared one means one check serves everyone — so it stays more up to date — and you avoid a duplicate.',
          )
        : null,
      hasSuggestions ? (
        <List gap="2xs" rootAttributes={{ 'data-suggest-similar': '' }}>
          {suggest.suggestions.map((sug) => (
            <ListItem
              action="suggest-follow"
              label={sug.name}
              disabled={sug.mine}
              multiline
              status={
                sug.mine
                  ? 'Following'
                  : `${String(sug.subscriberCount)} ${sug.subscriberCount === 1 ? 'follower' : 'followers'}`
              }
              trailing={[
                suggest.strongMatch?.id === sug.id ? (
                  <Badge size="compact" tone="info">
                    Same Subject
                  </Badge>
                ) : null,
                sug.catalog ? (
                  <Badge size="compact" appearance="outline">
                    Catalog
                  </Badge>
                ) : null,
              ]}
              rootAttributes={{ 'data-key': sug.id, 'data-suggest-follow': sug.name }}
            />
          ))}
        </List>
      ) : null,
      hasSuggestions
        ? hint('Catalog topics don’t count toward your custom-topic limit (some plans include a catalog limit too).')
        : null,
    ],
    footer: [
      <Button emphasis="secondary" action="suggest-dismiss">
        Cancel
      </Button>,
      <Button tone="brand" rootAttributes={{ 'data-suggest-add': suggest.name }}>
        {`Add “${suggest.name}” Anyway`}
      </Button>,
    ],
  });
}

/**
 * The topic's focus angles (NM-27): a toggle (checkbox) `ChoiceChips` group of the fixed
 * angle vocabulary. Uncontrolled after open — seeded from `topic.focus`; the
 * surrounding `data-morph-skip-children` keeps a poll's morph from resetting a
 * chip the user has ticked but not yet saved.
 */
export function renameFocusJsx(topic: Topic): SafeHtml {
  // The chips' values are the vocabulary's own ids; a stored id the vocabulary
  // no longer has simply isn't ticked (it has no chip to tick).
  const ids = FOCUS_OPTIONS.map((option) => option.id);
  const selected = ids.filter((id) => topic.focus.includes(id));
  return (
    <div data-focus-options="" data-morph-skip-children>
      <List gap="xs">
        <FieldLabel id="rename-angles-label">Angles</FieldLabel>
        {hint(
          'Optional. Narrow this topic to the angles you care about — which part of the subject to cover. Applies from the next check.',
        )}
        <ChoiceChips
          name="focus"
          labelledBy="rename-angles-label"
          multiple="toggle"
          size="compact"
          value={selected}
          choices={FOCUS_OPTIONS.map((option) => ({ value: option.id, label: option.label }))}
        />
      </List>
    </div>
  );
}

/**
 * The topic's disambiguating **sense**, read-only (NM-163/NM-165), shown above
 * the angles to make the two-tier steer visible: the sense says *which subject*
 * the name means; the angles say *which slice* of it to cover.
 */
export function renameSenseJsx(topic: Topic): SafeHtml {
  if (topic.chosenSense === null) return <span />;
  return (
    <Row gap="xs" wrap vAlign="middle" textInsets="rl" rootAttributes={{ 'data-sense-note': '' }}>
      <Text variant="span" size="compact" tone="quiet">
        Sense
      </Text>
      <Badge size="compact" tone="pop">
        {topic.chosenSense}
      </Badge>
      <Text variant="span" size="compact" tone="quiet">
        Which meaning of this ambiguous name you’re following.
      </Text>
    </Row>
  );
}

/**
 * The subject select, or nothing (NEWS-407, FR-22.7a). Its own function so the
 * section change re-renders just this container, not the half-typed form; and
 * absent rather than disabled for a section with no subjects.
 */
export function renameSubcategoryJsx(categorySlug: string | null, subSlug: string | null): SafeHtml {
  const category = findCategory(activeCategories(BUILTIN_CATEGORIES), categorySlug);
  const subs = category?.subcategories.filter((s) => !s.retired) ?? [];
  if (subs.length === 0) return <span />;
  return (
    <Select
      name="topic-subcategory"
      fitMenu
      label="Subject"
      value={subSlug ?? ''}
      choices={[
        { value: '', label: 'No Particular Subject' },
        ...subs.map((sub) => ({ value: sub.slug, label: sub.label })),
      ]}
    />
  );
}

/**
 * Edit a topic (NEWS-139; "edit", not "rename", NEWS-162): the name — which is
 * the question put to the model — its section (NEWS-407), its read-only sense
 * (NM-165) and its focus angles (NM-27), saved together in one PATCH. The
 * clear-stories option is offered only when there are stories to clear, and
 * is off by default.
 */
export function renameDialogJsx(
  topic: Topic,
  itemCount: number | null,
  categorySlug: string | null,
  subSlug: string | null,
): SafeHtml {
  return appDialog({
    key: 'rename',
    label: `Edit “${topic.name}”`,
    lightDismiss: false,
    body: [
      <form id="rename-form" data-save-rename={topic.id}>
        <List gap="m">
          {hint(
            'The name is what the model is asked about, so changing it changes what gets found from the next check onwards.',
          )}
          <TextField name="topic-name" label="Name" maxLength={200} autocomplete="off" value={topic.name} />
          {/* Section (NEWS-407, FR-22.7a). "Let Newsmonger decide" clears the
              section *and* makes the topic eligible for automatic
              classification again (FR-22.7). */}
          <Select
            name="topic-category"
            fitMenu
            label="Section"
            value={categorySlug ?? ''}
            choices={[
              { value: '', label: 'Let Newsmonger Decide' },
              ...activeCategories(BUILTIN_CATEGORIES).map((category) => ({
                value: category.slug,
                label: category.label,
              })),
            ]}
          />
          <div data-rename-subsection="">{renameSubcategoryJsx(categorySlug, subSlug)}</div>
          <div data-rename-sense="">{renameSenseJsx(topic)}</div>
          {renameFocusJsx(topic)}
          {/* `null` means the count hasn't arrived yet. */}
          <div data-rename-clear="">
            {itemCount !== null && itemCount > 0 ? (
              <Checkbox
                name="clear-items"
                label={`Also clear the ${String(itemCount)} ${itemCount === 1 ? 'story' : 'stories'} already found for this topic`}
              />
            ) : (
              ''
            )}
          </div>
        </List>
      </form>,
    ],
    footer: [
      <Button emphasis="secondary" action="close-rename">
        Cancel
      </Button>,
      <Button tone="brand" type="submit" form="rename-form">
        Save
      </Button>,
    ],
  });
}

/**
 * The Add Topic dialog (NM-ZJT0H7) — the whole add flow, opened from the feed
 * toolbar's "Add Topic…" or the empty state. It used to be an inline form at
 * the foot of the rail. The form keeps its `add-topic-form` action, so the
 * submit path (the instant "Adding…" placeholder, the did-you-mean check) is
 * the one it always was; submitting closes this dialog first, so a did-you-mean
 * dialog that follows never stacks on it. No light dismiss: it holds typing.
 */
export function addTopicDialogJsx(): SafeHtml {
  return appDialog({
    key: 'add-topic',
    label: 'Add a Topic',
    size: 'small',
    lightDismiss: false,
    body: [
      <form id="add-topic-form" data-action="add-topic-form" class="add-topic">
        <List gap="m">
          {hint(
            'Name anything — a company, a place, or a question. Newsmonger checks it on your schedule and reports only what’s new.',
          )}
          <TextField
            name="topic-name"
            label="Topic"
            placeholder="A company, a place, or a question"
            maxLength={200}
            autocomplete="off"
            autofocus
            hint="e.g. solid-state batteries, Lisbon transport, is fusion close?"
          />
        </List>
      </form>,
    ],
    footer: [
      <Button emphasis="secondary" action="close-add-topic">
        Cancel
      </Button>,
      <Button tone="brand" type="submit" form="add-topic-form">
        Add Topic
      </Button>,
    ],
  });
}

/**
 * The upgrade checkout (NM-225/226) — a **mock** payment form, so the flow is
 * real and testable without a payment processor. The tiers priced above the
 * current plan, a card field, and the test cards that drive each outcome.
 */
export function checkoutDialogJsx(
  checkout: CheckoutState,
  currentPlanId: string,
  billing: StateResp['billing'],
): SafeHtml {
  const current = billing.plans.find((p) => p.id === currentPlanId);
  const currentPrice = current?.priceUsd ?? 0;
  // Only tiers priced above the current one are upgrades (the server enforces this too).
  const upgrades = billing.plans.filter((p) => p.priceUsd > currentPrice);
  const selected = upgrades.find((p) => p.id === checkout.plan) ?? null;

  return appDialog({
    key: 'checkout',
    label: 'Upgrade Your Plan',
    lightDismiss: false,
    body: [
      hint(
        <>
          Pick a plan and pay with a test card — nothing is really charged.{' '}
          <a href="/pricing" target="_blank" rel="noopener noreferrer">
            See full pricing
          </a>
          .
        </>,
      ),
      <List gap="2xs" rootAttributes={{ 'data-checkout-plans': '' }}>
        {upgrades.map((p) => (
          <ListItem
            action="checkout-plan"
            label={`${p.label} — $${p.priceUsd.toFixed(2)}/mo`}
            description={`${String(p.customCap)} custom · ${p.catalogCap === null ? 'unlimited' : String(p.catalogCap)} catalog${
              p.checkIntervalMs === undefined ? '' : ` · ${cadenceLine(p.checkIntervalMs).toLowerCase()}`
            }`}
            pressed={checkout.plan === p.id}
            multiline
            rootAttributes={{ 'data-key': p.id, 'data-checkout-plan': p.id }}
          />
        ))}
      </List>,
      // Uncontrolled: no `value`, so a poll's morph never resets a typed number,
      // and the number never lingers in app state.
      <TextField
        name="checkout-card"
        label="Card Number"
        inputMode="numeric"
        autocomplete="off"
        placeholder="4242 4242 4242 4242"
        disabled={checkout.submitting}
      />,
      <Disclosure title="Test Cards" appearance="framed" rootAttributes={{ 'data-test-cards': '' }}>
        <List gap="2xs">
          {MOCK_TEST_CARDS.map((card) => (
            <Text size="compact" flush>
              <code>{card.number}</code> — {card.outcome}
            </Text>
          ))}
        </List>
      </Disclosure>,
      <div data-checkout-error="" aria-live="polite">
        {checkout.error === null ? (
          ''
        ) : (
          <StateBanner tone="danger" urgency="alert" icon={icon('warn')} title={checkout.error} />
        )}
      </div>,
    ],
    footer: [
      <Button emphasis="secondary" action="close-checkout">
        Cancel
      </Button>,
      <Button tone="brand" action="submit-checkout" disabled={selected === null || checkout.submitting}>
        {checkout.submitting
          ? 'Processing…'
          : selected === null
            ? 'Choose a Plan'
            : `Pay $${selected.priceUsd.toFixed(2)}`}
      </Button>,
    ],
  });
}
